import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

import { T, FONT_MONO, PILL_SHADOW } from '../../../constants/tokens';

const useStyles = makeStyles({
  label: {
    fontFamily: FONT_MONO,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
    textTransform: 'uppercase',
    color: T.body,
    marginBottom: 8
  },
  trigger: {
    display: 'flex',
    width: '100%',
    minWidth: 0,
    alignItems: 'center',
    gap: 8,
    height: 40,
    padding: '0 12px',
    border: '1px solid ' + T.hairline,
    borderRadius: 6,
    background: T.elevated,
    color: T.ink,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': { background: T.surfaceSoft },
    '&:focus-visible': { outline: '2px solid ' + T.ink, outlineOffset: 2 }
  },
  menu: {
    position: 'absolute',
    top: 46,
    left: 0,
    zIndex: 20,
    width: 'max(240px, 100%)',
    background: T.elevated,
    border: '1px solid ' + T.hairline,
    borderRadius: 6,
    boxShadow: '0 12px 32px rgba(35,37,29,0.14)',
    padding: 6
  },
  option: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    border: 0,
    borderRadius: 6,
    background: 'transparent',
    color: T.ink,
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': { background: T.surfaceSoft }
  },
  floorTrack: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: T.pillTrack,
    borderRadius: 6,
    padding: 4
  },
  floor: {
    fontFamily: FONT_MONO,
    fontSize: 14,
    fontWeight: 500,
    minWidth: 36,
    height: 32,
    padding: '0 10px',
    border: 0,
    borderRadius: 6,
    cursor: 'pointer',
    background: 'transparent',
    color: T.body,
    '&:hover:not($floorOn)': { background: T.railHover, color: T.ink },
    '&:focus-visible': { outline: '2px solid ' + T.ink, outlineOffset: 2 }
  },
  floorOn: {
    background: T.pillActive,
    color: T.ink,
    boxShadow: PILL_SHADOW
  }
});

/**
 * Building and floor pickers for the live map.
 *
 * Building is a dropdown and floor is a compact pill group, as the design has
 * them. A school can have many buildings, which is why that one is a menu,
 * while floors are few and switched constantly, so they stay on screen.
 */
export default function SelectorWidget({
  buildings,
  onBuildingSelection,
  onFloorSelection,
  selectedBuilding,
  selectedFloorID
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    // Initially select a floor
    if (selectedBuilding != null && selectedFloorID == null && selectedBuilding?.getFloors()?.length > 0) {
      onFloorSelection(selectedBuilding?.getFloors()[0]?.getID());
    }
  }, [buildings, onBuildingSelection, onFloorSelection, selectedBuilding, selectedFloorID]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const floors = selectedBuilding?.getFloors() ?? [];

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div className={classes.label}>Building</div>
        <div ref={wrapRef} style={{ position: 'relative', width: '100%', maxWidth: 320 }}>
          <button
            type='button'
            aria-label='Choose building'
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className={classes.trigger}
          >
            <span
              style={{
                minWidth: 0,
                flex: '1 1 auto',
                textAlign: 'left',
                fontSize: 14,
                fontWeight: 500,
                lineHeight: '20px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {selectedBuilding?.getName?.() ?? 'Select a building'}
            </span>
            <svg width='10' height='10' viewBox='0 0 10 10' fill='none' stroke='currentColor' strokeWidth='1.3' strokeLinecap='round' strokeLinejoin='round' style={{ flex: 'none', opacity: 0.55 }}>
              <path d='M2 4l3 3 3-3' />
            </svg>
          </button>

          {open &&
            <div className={classes.menu}>
              {buildings?.map((building, index) => {
                const on = selectedBuilding?.getID?.() === building.getID();
                return (
                  <button
                    type='button'
                    key={index}
                    className={classes.option}
                    style={on ? { background: T.surfaceSoft } : undefined}
                    onClick={() => {
                      onBuildingSelection(building);
                      setOpen(false);
                    }}
                  >
                    <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {building.getName()}
                    </span>
                    <span style={{ flex: 'none', fontFamily: FONT_MONO, fontSize: 12, color: T.body }}>
                      {building.getFloors()?.length ?? 0}
                    </span>
                    <span style={{ flex: 'none', width: 12, fontSize: 12, color: T.ink }}>
                      {on ? '✓' : ''}
                    </span>
                  </button>
                );
              })}
            </div>}
        </div>
      </div>

      {floors.length > 0 &&
        <div style={{ flex: '0 1 auto' }}>
          <div className={classes.label}>Floor</div>
          <div className={classes.floorTrack}>
            {floors.map((floor, index) => {
              const on = selectedFloorID === floor.getID();
              return (
                <button
                  type='button'
                  key={index}
                  aria-current={on ? 'page' : undefined}
                  className={[classes.floor, on ? classes.floorOn : ''].filter(Boolean).join(' ')}
                  onClick={() => onFloorSelection(floor.getID())}
                >
                  {floor.getIdentifier()}
                </button>
              );
            })}
          </div>
        </div>}
    </div>
  );
}

SelectorWidget.propTypes = {
  buildings: PropTypes.array,
  onBuildingSelection: PropTypes.func,
  onFloorSelection: PropTypes.func,
  selectedBuilding: PropTypes.object,
  selectedFloorID: PropTypes.number
};
